import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Share2, Copy, CheckCircle, ExternalLink, Mail, Users, X } from 'lucide-react';
import { analysesApi } from '@/lib/api';
import { toast } from '@/components/ui/sonner';

const fmt = (v) => {
  if (v === undefined || v === null || isNaN(v)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
};
const pct = (v) => v !== undefined && v !== null ? `${(v * 100).toFixed(1)}%` : '—';

export default function SharedViewPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('share');
    if (token) {
      loadShared(token);
    } else {
      setLoading(false);
    }
  }, []);

  const loadShared = async (token) => {
    try {
      const res = await analysesApi.getShared(token);
      setData(res.data);
    } catch (e) {
      setError('Shared analysis not found or link has expired.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <p className="text-red-400 text-sm">{error}</p>
      </div>
    );
  }

  if (!data) return null;

  const results = data.results || {};
  const dc = results.deal_comparison || {};
  const dcf = results.dcf || {};
  const scenarios = results.scenarios || {};
  const flags = results.trend_flags || [];
  const insights = data.ai_insights?.insights || '';

  return (
    <div className="space-y-6 animate-fade-in max-w-5xl mx-auto" data-testid="shared-view">
      <div className="text-center py-4">
        <Badge variant="outline" className="border-indigo-500/30 text-indigo-400 mb-2">Read-Only Shared View</Badge>
        <h1 className="text-2xl font-semibold text-zinc-100 font-['Manrope']">{data.name}</h1>
        <p className="text-xs text-zinc-500 mt-1">Generated {new Date(data.updated_at).toLocaleDateString()}</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-zinc-900/60 border-indigo-500/20">
          <CardContent className="p-4">
            <span className="text-xs text-zinc-400">Enterprise Value (DCF)</span>
            <div className="font-mono text-2xl font-bold text-indigo-400">{fmt(dcf.enterprise_value)}</div>
            <span className="text-xs text-zinc-500">Implied {dcf.implied_multiple?.toFixed(2)}x multiple</span>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/60 border-emerald-500/20">
          <CardContent className="p-4">
            <span className="text-xs text-zinc-400">Buyer DSCR</span>
            <div className="font-mono text-2xl font-bold text-emerald-400">{dc.buyer?.dscr?.toFixed(2)}x</div>
            <span className="text-xs text-zinc-500">{dc.buyer?.dscr >= 1.35 ? 'Above SBA threshold' : 'Below SBA 1.35x'}</span>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/60 border-zinc-800/60">
          <CardContent className="p-4">
            <span className="text-xs text-zinc-400">FCF to Buyer</span>
            <div className="font-mono text-2xl font-bold text-zinc-100">{fmt(dc.buyer?.fcf_to_buyer)}</div>
            <span className="text-xs text-zinc-500">Annual free cash flow</span>
          </CardContent>
        </Card>
      </div>

      {/* Deal Comparison */}
      {(dc.seller || dc.buyer) && (
        <Card className="bg-zinc-900/60 border-zinc-800/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-zinc-300">Deal Comparison</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-800/60">
                  <th className="text-left text-xs text-zinc-400 px-4 py-2">Metric</th>
                  <th className="text-right text-xs text-zinc-400 px-4 py-2">Seller</th>
                  <th className="text-right text-xs text-zinc-400 px-4 py-2">Buyer</th>
                  <th className="text-right text-xs text-zinc-400 px-4 py-2">Comparable</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { label: 'Purchase Price', key: 'purchase_price' },
                  { label: 'Total Deal Size', key: 'total_deal_size' },
                  { label: 'SDE Multiple', key: 'sde_multiple', format: (v) => v?.toFixed(2) + 'x' },
                  { label: 'DSCR', key: 'dscr', format: (v) => v?.toFixed(2) + 'x' },
                  { label: 'FCF to Buyer', key: 'fcf_to_buyer' },
                ].map(r => (
                  <tr key={r.key} className="border-b border-zinc-800/40">
                    <td className="text-xs text-zinc-300 px-4 py-2">{r.label}</td>
                    <td className="text-right font-mono text-xs text-zinc-200 px-4 py-2">{r.format ? r.format(dc.seller?.[r.key]) : fmt(dc.seller?.[r.key])}</td>
                    <td className="text-right font-mono text-xs text-zinc-200 px-4 py-2">{r.format ? r.format(dc.buyer?.[r.key]) : fmt(dc.buyer?.[r.key])}</td>
                    <td className="text-right font-mono text-xs text-zinc-200 px-4 py-2">{r.format ? r.format(dc.comparable?.[r.key]) : fmt(dc.comparable?.[r.key])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Scenarios */}
      {Object.keys(scenarios).length > 0 && (
        <Card className="bg-zinc-900/60 border-zinc-800/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-zinc-300">Scenario Analysis</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(scenarios).map(([name, s]) => {
                const askingPrice = dc.seller?.purchase_price || 449000;
                const supports = s.enterprise_value > askingPrice;
                return (
                  <div key={name} className={`p-3 rounded-lg border ${supports ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-red-500/20 bg-red-500/5'}`}>
                    <div className="text-xs text-zinc-400 mb-1">{name}</div>
                    <div className="font-mono text-lg text-zinc-100">{fmt(s.enterprise_value)}</div>
                    <Badge variant="outline" className={`mt-1 text-[10px] ${supports ? 'border-emerald-500/30 text-emerald-400' : 'border-red-500/30 text-red-400'}`}>
                      {supports ? 'Supports deal' : 'Below asking'}
                    </Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Trend Flags */}
      {flags.length > 0 && (
        <Card className="bg-zinc-900/60 border-zinc-800/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-zinc-300">Key Findings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {flags.map((f, i) => (
                <div key={i} className="flex items-start gap-2 text-xs py-1">
                  <span className={`w-1.5 h-1.5 rounded-full mt-1 shrink-0 ${f.type === 'positive' ? 'bg-emerald-400' : 'bg-red-400'}`} />
                  <span className="text-zinc-500 font-mono w-16 shrink-0">{f.category}</span>
                  <span className="text-zinc-300">{f.message}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* AI Insights */}
      {insights && (
        <Card className="bg-zinc-900/60 border-indigo-500/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-zinc-300">AI Deal Analysis</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="whitespace-pre-wrap text-sm text-zinc-300 leading-relaxed">{insights}</div>
          </CardContent>
        </Card>
      )}

      <div className="text-center text-xs text-zinc-600 py-4">
        Powered by AcquiFlow
      </div>
    </div>
  );
}

// Share button component used in the header/dashboard
export function ShareButton({ analysisId }) {
  const [loading, setLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [shareToken, setShareToken] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [showUserShare, setShowUserShare] = useState(false);
  const [emailData, setEmailData] = useState({ email: '', note: '', sender_name: '' });
  const [emailSending, setEmailSending] = useState(false);

  const handleShare = useCallback(async () => {
    if (!analysisId) return;
    setLoading(true);
    try {
      const res = await analysesApi.share(analysisId);
      const token = res.data.share_token;
      setShareToken(token);
      const url = `${window.location.origin}?share=${token}`;
      setShareUrl(url);
    } catch (e) {
      toast.error('Failed to generate share link');
    } finally {
      setLoading(false);
    }
  }, [analysisId]);

  const copyLink = () => {
    if (!shareUrl) return;
    try {
      const input = document.createElement('input');
      input.value = shareUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      toast.success('Share link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.info('Copy the share link from the field');
    }
  };

  const handleEmailShare = useCallback(async () => {
    if (!emailData.email || !analysisId) return;
    setEmailSending(true);
    try {
      const res = await analysesApi.emailShare(analysisId, emailData);
      const { subject, email, note, analysis_name } = res.data;
      const shareLink = shareUrl || `${window.location.origin}?share=${res.data.share_token}`;
      const body = `${emailData.sender_name || 'I'} shared an analysis: ${analysis_name}\n\n${note ? note + '\n\n' : ''}View here:\n${shareLink}\n\nPowered by AcquiFlow`;
      window.open(`mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
      toast.success('Email client opened');
      setShowEmail(false);
    } catch (e) {
      toast.error('Failed to prepare email');
    } finally {
      setEmailSending(false);
    }
  }, [emailData, analysisId, shareUrl]);

  return (
    <div className="flex items-center gap-2">
      {!shareUrl ? (
        <div className="flex items-center gap-1.5">
          <Button variant="outline" size="sm" onClick={handleShare} disabled={loading || !analysisId} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800" data-testid="share-btn">
            {loading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5 mr-1.5" />}
            Share Link
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowUserShare(true)} disabled={!analysisId} className="border-indigo-500/30 text-indigo-400 hover:bg-indigo-500/10" data-testid="share-with-user-btn">
            <Users className="w-3.5 h-3.5 mr-1.5" />
            Share with User
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-1.5">
          <div className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-xs font-mono text-zinc-300 max-w-[160px] truncate">{shareUrl}</div>
          <Button variant="ghost" size="sm" onClick={copyLink} className="text-zinc-400 hover:text-zinc-200 px-2" data-testid="copy-share-link">
            {copied ? <CheckCircle className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => window.open(shareUrl, '_blank')} className="text-zinc-400 hover:text-zinc-200 px-2" data-testid="open-share-link">
            <ExternalLink className="w-3.5 h-3.5" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowEmail(!showEmail)} className="text-zinc-400 hover:text-indigo-300 px-2" data-testid="email-share-toggle">
            <Mail className="w-3.5 h-3.5" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowUserShare(true)} className="border-indigo-500/30 text-indigo-400 hover:bg-indigo-500/10 ml-2" data-testid="share-with-user-btn">
            <Users className="w-3.5 h-3.5 mr-1.5" />
            Users
          </Button>
        </div>
      )}
      {showEmail && shareUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowEmail(false)}>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl shadow-2xl p-5 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()} data-testid="email-share-dialog">
            <h3 className="text-sm font-semibold text-zinc-200 mb-3 font-['Manrope']">Email Share Link</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Your Name</label>
                <input type="text" value={emailData.sender_name} onChange={(e) => setEmailData(p => ({ ...p, sender_name: e.target.value }))} placeholder="David" className="w-full bg-zinc-800 border border-zinc-700 rounded-md text-sm text-zinc-200 px-3 py-1.5 focus:outline-none focus:border-indigo-500/50" data-testid="email-sender-name" />
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Recipient Email</label>
                <input type="email" value={emailData.email} onChange={(e) => setEmailData(p => ({ ...p, email: e.target.value }))} placeholder="partner@company.com" className="w-full bg-zinc-800 border border-zinc-700 rounded-md text-sm text-zinc-200 px-3 py-1.5 focus:outline-none focus:border-indigo-500/50" data-testid="email-recipient" />
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Personal Note (optional)</label>
                <textarea value={emailData.note} onChange={(e) => setEmailData(p => ({ ...p, note: e.target.value }))} placeholder="Here's the analysis for the Carpets 4 U deal..." rows={3} className="w-full bg-zinc-800 border border-zinc-700 rounded-md text-sm text-zinc-200 px-3 py-1.5 focus:outline-none focus:border-indigo-500/50 resize-none" data-testid="email-note" />
              </div>
              <div className="flex gap-2 pt-1">
                <Button onClick={handleEmailShare} disabled={emailSending || !emailData.email} className="flex-1 bg-indigo-600 hover:bg-indigo-500 text-white" data-testid="send-email-btn">
                  {emailSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                  Open Email Client
                </Button>
                <Button variant="outline" onClick={() => setShowEmail(false)} className="border-zinc-700 text-zinc-400">Cancel</Button>
              </div>
              <p className="text-[10px] text-zinc-500">Opens your default email client with a pre-filled message.</p>
            </div>
          </div>
        </div>
      )}
      {showUserShare && (
        <ShareWithUsersModal analysisId={analysisId} onClose={() => setShowUserShare(false)} />
      )}
    </div>
  );
}

// Modal for sharing with specific users (clients)
function ShareWithUsersModal({ analysisId, onClose }) {
  const [users, setUsers] = useState([]);
  const [sharedWith, setSharedWith] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [accessLevel, setAccessLevel] = useState('step1_only');
  const [emailPrompt, setEmailPrompt] = useState(null); // For email notification prompt

  // Load users and current shares
  useEffect(() => {
    const loadData = async () => {
      try {
        const token = localStorage.getItem('acquiflow_token');
        const [usersRes, sharedRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/users`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          analysesApi.getSharedUsers(analysisId)
        ]);
        
        if (usersRes.ok) {
          const usersData = await usersRes.json();
          // Filter to only show client users
          setUsers(usersData.filter(u => u.role === 'client'));
        }
        
        setSharedWith(sharedRes.data || []);
      } catch (e) {
        console.error('Failed to load data:', e);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [analysisId]);

  const handleShare = async () => {
    if (!selectedUser) return;
    setSharing(true);
    try {
      const res = await analysesApi.shareWithUser(analysisId, selectedUser, accessLevel);
      toast.success('Analysis shared with user');
      
      // Show email notification prompt
      if (res.data?.email_data) {
        setEmailPrompt(res.data.email_data);
      }
      
      // Refresh shared list
      const sharedRes = await analysesApi.getSharedUsers(analysisId);
      setSharedWith(sharedRes.data || []);
      setSelectedUser('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to share');
    } finally {
      setSharing(false);
    }
  };

  const handleUnshare = async (userId) => {
    try {
      await analysesApi.unshareWithUser(analysisId, userId);
      toast.success('User access removed');
      setSharedWith(prev => prev.filter(s => s.user_id !== userId));
    } catch (e) {
      toast.error('Failed to remove access');
    }
  };

  const availableUsers = users.filter(u => !sharedWith.some(s => s.user_id === u.id));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl shadow-2xl p-5 w-full max-w-lg mx-4" onClick={(e) => e.stopPropagation()} data-testid="share-users-modal">
        <h3 className="text-sm font-semibold text-zinc-200 mb-4 font-['Manrope']">Share with Users</h3>
        
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-indigo-400 animate-spin" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Add new share */}
            {availableUsers.length > 0 ? (
              <div className="space-y-3 p-3 rounded-lg border border-zinc-800 bg-zinc-900/50">
                <div>
                  <label className="block text-xs text-zinc-400 mb-1.5">Select User</label>
                  <select
                    value={selectedUser}
                    onChange={(e) => setSelectedUser(e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-md text-sm text-zinc-200 px-3 py-2 focus:outline-none focus:border-indigo-500/50"
                    data-testid="share-user-select"
                  >
                    <option value="">Choose a user...</option>
                    {availableUsers.map(u => (
                      <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs text-zinc-400 mb-1.5">Access Level</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setAccessLevel('step1_only')}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        accessLevel === 'step1_only'
                          ? 'border-indigo-500/50 bg-indigo-500/10'
                          : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600'
                      }`}
                      data-testid="access-step1-only"
                    >
                      <div className="text-xs font-medium text-zinc-200 mb-0.5">Step 1 Only</div>
                      <div className="text-[10px] text-zinc-500">Can view Deal, SDE, P&L, Solvency, Inventory, Working Capital</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setAccessLevel('full_access')}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        accessLevel === 'full_access'
                          ? 'border-indigo-500/50 bg-indigo-500/10'
                          : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600'
                      }`}
                      data-testid="access-full"
                    >
                      <div className="text-xs font-medium text-zinc-200 mb-0.5">Full Access</div>
                      <div className="text-[10px] text-zinc-500">Can view all analysis tabs (read-only)</div>
                    </button>
                  </div>
                </div>

                <Button
                  onClick={handleShare}
                  disabled={!selectedUser || sharing}
                  className="w-full bg-indigo-600 hover:bg-indigo-500 text-white"
                  data-testid="share-user-submit"
                >
                  {sharing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Share2 className="w-4 h-4 mr-2" />}
                  Share with User
                </Button>
              </div>
            ) : users.length === 0 ? (
              <p className="text-xs text-zinc-500 text-center py-4">No client users available. Create users in User Management first.</p>
            ) : (
              <p className="text-xs text-zinc-500 text-center py-4">All client users already have access to this analysis.</p>
            )}

            {/* Current shares */}
            {sharedWith.length > 0 && (
              <div>
                <h4 className="text-xs text-zinc-400 mb-2">Currently Shared With</h4>
                <div className="space-y-2">
                  {sharedWith.map(share => (
                    <div key={share.user_id} className="flex items-center justify-between p-2.5 rounded-lg border border-zinc-800 bg-zinc-900/40">
                      <div>
                        <div className="text-sm text-zinc-200">{share.user_name}</div>
                        <div className="text-[10px] text-zinc-500">{share.user_email}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={`text-[10px] ${
                          share.access_level === 'full_access'
                            ? 'border-emerald-500/30 text-emerald-400'
                            : 'border-amber-500/30 text-amber-400'
                        }`}>
                          {share.access_level === 'full_access' ? 'Full Access' : 'Step 1 Only'}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleUnshare(share.user_id)}
                          className="h-7 w-7 p-0 text-zinc-500 hover:text-red-400"
                          data-testid={`unshare-${share.user_id}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end pt-2">
              <Button variant="outline" onClick={onClose} className="border-zinc-700 text-zinc-400">Close</Button>
            </div>
          </div>
        )}
        
        {/* Email notification prompt */}
        {emailPrompt && (
          <div className="mt-4 p-4 rounded-lg border border-emerald-500/30 bg-emerald-500/5">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-medium text-emerald-300 mb-1">Analysis Shared Successfully!</h4>
                <p className="text-xs text-zinc-400 mb-3">
                  Would you like to notify <span className="text-zinc-200">{emailPrompt.recipient_name}</span> via email?
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => {
                      const subject = `${emailPrompt.sharer_name} shared an analysis with you: ${emailPrompt.analysis_name}`;
                      const body = `Hi ${emailPrompt.recipient_name},\n\n${emailPrompt.sharer_name} has shared the analysis "${emailPrompt.analysis_name}" with you.\n\nAccess Level: ${emailPrompt.access_description}\n\nYou can view it by logging into AcquiFlow at:\n${window.location.origin}\n\nBest regards,\n${emailPrompt.sharer_name}`;
                      window.open(`mailto:${emailPrompt.recipient_email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
                      setEmailPrompt(null);
                      toast.success('Email client opened');
                    }}
                    className="bg-emerald-600 hover:bg-emerald-500 text-white"
                    data-testid="send-share-notification"
                  >
                    <Mail className="w-3.5 h-3.5 mr-1.5" />
                    Send Email
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEmailPrompt(null)}
                    className="border-zinc-600 text-zinc-400"
                  >
                    Skip
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
